import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileCheck2,
  Banknote,
  FileText,
  XCircle,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import type { TypeFilterTab } from '@/components/modules/ModuleHeader';

// ============================================================================
// TYPE COERCION HELPERS (I3)
// Shared across all inbox components to avoid duplication.
// ============================================================================

export function asObject(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  return {};
}

export function asString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

export function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

// ============================================================================
// STATUS FILTER OPTIONS (D8)
// Centralised filter options and badge tones for all finance inbox components.
// Status labels should come from SSOT via financeStatusConfig where possible.
// ============================================================================

export interface StatusFilterOption {
  value: string;
  label: string;
  color: string;
  icon: LucideIcon;
}

export const STATUS_FILTER_OPTIONS: StatusFilterOption[] = [
  { value: 'new', label: 'New', color: 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-200', icon: Sparkles },
  { value: 'processing', label: 'Processing', color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-200', icon: CheckCircle2 },
  { value: 'to_review', label: 'Review', color: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200', icon: AlertTriangle },
  { value: 'ready', label: 'Ready', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200', icon: CheckCircle2 },
  { value: 'pending_approval', label: 'Pending Approval', color: 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-200', icon: Clock },
  { value: 'paid', label: 'Paid', color: 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-200', icon: Banknote },
  { value: 'completed', label: 'Completed', color: 'bg-teal-100 text-teal-700 dark:bg-teal-950/40 dark:text-teal-200', icon: FileCheck2 },
];

/**
 * Expense Inbox status filter options — the 5 status sub-tabs shown in the
 * Expense Inbox view. The 6th sub-tab ("Anomaly") is handled separately in
 * ExpenseInbox.tsx because it renders AnomalyReviewQueue instead of a
 * filtered work-items list.
 *
 * "Pending Approval" is conditionally hidden when workflowMode === 'simplified'
 * (no approval queue in simplified mode).
 */
export const EXPENSE_INBOX_STATUS_OPTIONS: StatusFilterOption[] = [
  { value: 'new', label: 'New', color: 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-200', icon: Sparkles },
  { value: 'draft', label: 'Draft', color: 'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-200', icon: FileText },
  { value: 'pending_approval', label: 'Pending Approval', color: 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-200', icon: Clock },
  { value: 'completed', label: 'Completed', color: 'bg-teal-100 text-teal-700 dark:bg-teal-950/40 dark:text-teal-200', icon: FileCheck2 },
  { value: 'void', label: 'Void', color: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300', icon: XCircle },
];

export const STATEMENT_INBOX_FILTER_TABS: TypeFilterTab[] = [
  { id: 'new', label: 'New', icon: Sparkles },
  { id: 'processing', label: 'Processing', icon: CheckCircle2 },
  { id: 'to_review', label: 'Review', icon: AlertTriangle },
  { id: 'ready', label: 'Ready', icon: CheckCircle2 },
  { id: 'pending_approval', label: 'Pending Approval', icon: Clock },
  { id: 'paid', label: 'Paid', icon: Banknote },
  { id: 'completed', label: 'Completed', icon: FileCheck2 },
];

// ============================================================================
// AGING THRESHOLD CONFIG (D10)
// Configurable aging thresholds for inbox badges. Defaults match the previous
// hardcoded values (2 days amber, 5 days red). Can later be moved to tenant settings.
// ============================================================================

export interface AgingThresholdConfig {
  amberDays: number;
  redDays: number;
}

const DEFAULT_AGING_THRESHOLDS: AgingThresholdConfig = {
  amberDays: 2,
  redDays: 5,
};

function resolveAgingThresholds(): AgingThresholdConfig {
  const amberRaw = typeof process !== 'undefined' ? process.env?.NEXT_PUBLIC_AGING_AMBER_DAYS : undefined;
  const redRaw = typeof process !== 'undefined' ? process.env?.NEXT_PUBLIC_AGING_RED_DAYS : undefined;
  const amberDays = amberRaw ? Number(amberRaw) : NaN;
  const redDays = redRaw ? Number(redRaw) : NaN;
  return {
    amberDays: Number.isFinite(amberDays) && amberDays > 0 ? amberDays : DEFAULT_AGING_THRESHOLDS.amberDays,
    redDays: Number.isFinite(redDays) && redDays > 0 ? redDays : DEFAULT_AGING_THRESHOLDS.redDays,
  };
}

export const AGING_THRESHOLDS: AgingThresholdConfig = resolveAgingThresholds();

export function getAgingBadge(
  createdAt: string | undefined,
  config: AgingThresholdConfig = AGING_THRESHOLDS,
): { label: string; cls: string } | null {
  if (!createdAt) return null;
  const ageMs = Date.now() - new Date(createdAt).getTime();
  const ageDays = ageMs / (1000 * 60 * 60 * 24);
  if (ageDays >= config.redDays) return { label: `${Math.floor(ageDays)}d`, cls: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-200' };
  if (ageDays >= config.amberDays) return { label: `${Math.floor(ageDays)}d`, cls: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200' };
  return null;
}

// ============================================================================
// OCR CONFIDENCE HELPERS (D9)
// Shared confidence chip rendering helpers for inbox list rows.
// ============================================================================

export interface ConfidenceBadge {
  label: string;
  cls: string;
}

export function getConfidenceBadge(confidence: number | null): ConfidenceBadge | null {
  if (confidence == null || !Number.isFinite(confidence)) return null;
  const pct = Math.round(confidence);
  if (pct >= 80) return { label: `${pct}%`, cls: 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-200' };
  if (pct >= 50) return { label: `${pct}%`, cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-200' };
  return { label: `${pct}%`, cls: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-200' };
}

// ============================================================================
// EXTRACTION SOURCE / PROVENANCE HELPERS
// Shared helpers for resolving which OCR/vision provider produced a document's
// AI extraction. Used to display "TV" (Total Vision) or "GV" (Gemini Vision)
// badges next to AI confidence indicators for quick troubleshooting.
// ============================================================================

export type ExtractionSource = 'total-vision' | 'gemini';

export interface ExtractionProvenance {
  source: ExtractionSource | null;
  model: string | null;
  confidence: number | null;
}

/**
 * Resolve the extraction provenance from a work item's custom_fields.
 *
 * Different intake channels store the provenance in different locations:
 *  - SmartTask capture: `custom_fields.extraction_provenance` (object with source/model/confidence)
 *  - Direct field: `custom_fields.extraction_source` / `custom_fields.extraction_model`
 *  - Extraction payload: `custom_fields.extraction_payload.extraction_source`
 *  - Receipt AI validation: `custom_fields.receipt_ai_validation.extraction_source`
 *
 * Returns `{ source: null, model: null, confidence: null }` when no provenance
 * is found (e.g. manually created documents with no AI extraction).
 */
export function getExtractionProvenance(
  customFields: Record<string, unknown> | null | undefined,
): ExtractionProvenance {
  const cf = asObject(customFields);

  // 1. SmartTask capture stores a structured provenance object
  const provenance = asObject(cf.extraction_provenance);
  if (provenance.source) {
    return {
      source: normalizeExtractionSource(provenance.source),
      model: asString(provenance.model),
      confidence: asNumber(provenance.confidence),
    };
  }

  // 2. Direct fields on custom_fields
  if (cf.extraction_source) {
    return {
      source: normalizeExtractionSource(cf.extraction_source),
      model: asString(cf.extraction_model),
      confidence: asNumber(cf.confidence) ?? asNumber(asObject(cf.extraction_payload).confidence),
    };
  }

  // 3. Inside extraction_payload
  const extractionPayload = asObject(cf.extraction_payload || cf.extraction);
  if (extractionPayload.extraction_source) {
    return {
      source: normalizeExtractionSource(extractionPayload.extraction_source),
      model: asString(extractionPayload.extraction_model),
      confidence: asNumber(extractionPayload.confidence) ?? asNumber(cf.confidence),
    };
  }

  // 4. Inside receipt_ai_validation
  const receiptAi = asObject(cf.receipt_ai_validation);
  if (receiptAi.extraction_source) {
    return {
      source: normalizeExtractionSource(receiptAi.extraction_source),
      model: asString(receiptAi.extraction_model),
      confidence: asNumber(receiptAi.confidence) ?? asNumber(cf.confidence),
    };
  }

  return { source: null, model: null, confidence: null };
}

function normalizeExtractionSource(value: unknown): ExtractionSource | null {
  const s = asString(value);
  if (!s) return null;
  const lower = s.toLowerCase();
  if (lower === 'total-vision' || lower === 'total_vision' || lower === 'tv') return 'total-vision';
  if (lower === 'gemini' || lower === 'gv') return 'gemini';
  return null;
}

/**
 * Short badge label for an extraction source: "TV" for Total Vision, "GV" for Gemini.
 * Returns null when the source is unknown (no AI extraction or unrecognized provider).
 */
export function getExtractionSourceLabel(source: ExtractionSource | null): string | null {
  if (source === 'total-vision') return 'TV';
  if (source === 'gemini') return 'GV';
  return null;
}

export interface ExtractionSourceBadgeInfo {
  label: string;
  cls: string;
  title: string;
}

/**
 * Returns badge styling info for an extraction source, or null when no badge
 * should be shown (no AI extraction or unrecognized provider).
 *
 * "TV" badges use a teal/indigo tone (self-hosted, private, preferred path).
 * "GV" badges use an amber/orange tone (external fallback).
 */
export function getExtractionSourceBadge(
  source: ExtractionSource | null,
  model?: string | null,
): ExtractionSourceBadgeInfo | null {
  const label = getExtractionSourceLabel(source);
  if (!label) return null;

  if (source === 'total-vision') {
    return {
      label,
      cls: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-200',
      title: `Extracted by Total Vision${model ? ` (${model})` : ''} — self-hosted OCR`,
    };
  }

  return {
    label,
    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200',
    title: `Extracted by Gemini Vision${model ? ` (${model})` : ''} — cloud fallback`,
  };
}
